import classnames from 'classnames';
import {
	BillingStatus,
	CostCodes,
	Data,
	FeatureFlags,
	Header as HeaderModule,
	Layout,
	Marketing,
	Modal as ModalModule,
	Print as PrintModule,
	SubscriptionPlan,
	Toast,
	UI,
	Utils,
} from 'common';
import AccountVerificationModal from 'common/src/code/accountVerification/AccountVerificationModal.component';
import { withIdentityVerification } from 'common/src/code/payment/hoc/withIdentityVerification';
import neutralizeCsvFormula from 'common/src/code/utils/neutralizeCsvFormula';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import moment from 'moment';
import React, { Component } from 'react';
import AdvancedSearchModal from './AdvancedSearchModal.component';
import CheckClaimStatusModal from './claimForm/CheckClaimStatusModal.component';
import FileClaimModal from './claimForm/FileClaimModal.component';
import InsuranceClaimFormModal from './claimForm/InsuranceClaimFormModal.component';
import Details from './Details.component';
import Grid from './Grid.component';
import LeftMenu from './LeftMenu.component';
import CreateReturnLabelModal from './returnLabel/CreateReturnLabelModal.component';
import { ActiveMenuItemId } from './shared/ActiveMenuItemId.enum';
import { DateRange } from './shared/DateRangeOptions';
import { EligibleFor, EligibleForParams } from './shared/EligibleForOptions';
import Print from './shared/Print';
import Toolbar from './Toolbar.component';
import Guide from './utils/Guide';
import HistoryCommunication from './utils/HistoryCommunication';
import HistoryUtil from './utils/Util';

const { Capability, Carrier, InternalCapability, SegmentEventTypes } = Data;
const { RegionType, CenterPanel, TopPanel, CollapsibleSidePanel } = Layout;
const {
	HttpStatusCode,
	Logger,
	Session,
	Util,
	BillingPlans,
	Analytics: AnalyticsAgent,
} = Utils;
const { openParcelGuardClaimForm } = Util;
const { Spinner } = UI;
const { Header, Analytics } = HeaderModule;
const { ManageCostCodesModal } = CostCodes;
const { MarketingCampaign } = Marketing;
const { BillingStatusModal } = BillingStatus;
const { LaunchDarklyUtil } = FeatureFlags;
const { UpgradePlanModal } = SubscriptionPlan;
const { ModalButtonActionType } = ModalModule;
const { PdfPreviewModal } = PrintModule;
const { withToast } = Toast;

class MainView extends Component {
	upgradeTrigger = '';

	constructor(props) {
		super(props);

		// calculate the start and end date range
		const activeMenuItem = this.getDefaultActiveMenuItem();
		const searchFilter =
			HistoryUtil.buildSearchFilterByActiveMenuItem(activeMenuItem);
		let showBillingStatusForPrint = true;
		let showEmailVerification = false;

		const {
			subscriptionStatus: {
				paymentHold,
				resubmit,
				adminHold,
				serviceFeesNotPaid,
			} = {},
			accountStatus,
		} = Session.getAccountInfo();

		// Checks to see if account is in a status that can print but not buy postage
		if (
			(paymentHold && !resubmit && !adminHold && !serviceFeesNotPaid) ||
			(paymentHold && resubmit && !adminHold && !serviceFeesNotPaid)
		) {
			showBillingStatusForPrint = false;
		}

		if (accountStatus === 'accountVerificationRequired') {
			showEmailVerification = true;
		}

		this.state = {
			totalCount: null,
			activeMenuItem,
			selectedPrints: [],
			prints: [],
			allPrints: [],
			pageSize: 100,
			costCodeOptions: [],
			isLoading: false,
			users: [],
			nextPageRequest: null,
			sortBy: null,
			searchFilter,
			isAdvanceSearch: false,
			headerTrigger: Util.getQueryParamByName('eligibleFor') || null,
			showBillingStatus: Session.showBillingStatusModal(),
			showBillingStatusForPrint:
				Session.showBillingStatusModal() && showBillingStatusForPrint,
			showEmailVerification,
		};
	}

	// call onSearchPrintsTrigger based on query params to apply filters
	componentDidMount() {
		const { headerTrigger, showBillingStatus, showEmailVerification } =
			this.state;
		const { globalpost = {} } = Session.getAccountInfo();
		const { firstMileCarrier = '' } = globalpost;
		const matchedItemValues = {
			[EligibleForParams.Refund]: EligibleFor.Refund,
			[EligibleForParams.Containerlabel]: EligibleFor.Containerlabel,
			[EligibleForParams.Manifest]: EligibleFor.Manifest,
			[EligibleForParams.InsuranceClaim]: EligibleFor.InsuranceClaim,
			[EligibleForParams.RefundAssist]: EligibleFor.RefundAssist,
		};
		const activeMenuItem = {
			activeMenuItemId: ActiveMenuItemId.EligibleFor,
			activeMenuItemValue: matchedItemValues[headerTrigger],
		};
		const {
			modalContext: { showModal },
		} = this.props;

		// do not apply container label filter if user isn't eligible
		if (
			headerTrigger === EligibleForParams.Containerlabel &&
			firstMileCarrier !== Carrier.USPS
		) {
			return;
		}

		// do not trigger for pickup, as that is not a search trigger
		if (headerTrigger && headerTrigger !== EligibleForParams.Pickup) {
			this.onSearchPrintsTrigger(activeMenuItem);
		}

		if (showBillingStatus) {
			showModal({ component: BillingStatusModal });
		}

		if (showEmailVerification) {
			showModal({
				component: AccountVerificationModal,
				data: {
					completionCallback: this.accountVerificationCallback,
					emailOnly: true,
				},
			});
		}

		const { ldClient } = this.props;
		if (ldClient) {
			Session.setFeatureFlags(ldClient.allFlags());
			LaunchDarklyUtil.logIntializeError(ldClient);
			const accountInfo = Session.getAccountInfo();
			AnalyticsAgent.initLogRocket(ldClient, accountInfo);
		}
	}

	getDefaultActiveMenuItem = () => ({
		activeMenuItemId: ActiveMenuItemId.DatePrinted,
		activeMenuItemValue: DateRange.Today,
		leftMenuTitle: 'Today',
	});

	fetchUsers = async () => {
		const {
			modalContext: { showError },
		} = this.props;
		const response = await HistoryCommunication.getUsers();

		if (HistoryCommunication.isSuccessful(response)) {
			this.setState({
				users: response.data.Users || [],
			});
		} else {
			showError({ response });
		}
	};

	/**
	 * Fetch cost codes.
	 */
	fetchCostCodes = async () => {
		const {
			modalContext: { showError },
		} = this.props;
		const response = await HistoryCommunication.getCostCodes();

		if (HistoryCommunication.isSuccessful(response)) {
			this.setState({
				costCodeOptions: Util.toCamelCase(response.data) || [],
			});
		} else {
			showError({ response });
		}
	};

	/**
	 * Make a call to getPrints API to fetch data.
	 * @param {object} payload
	 * @return {object}
	 */
	fetchPrints = async payload => {
		const {
			modalContext: { showError },
		} = this.props;

		// Temp fix for WEBAPPS-39108
		if (payload?.sort?.includes('from_address_postal_code')) {
			payload.sort = payload.sort.replace(
				'from_address_postal_code',
				'postal_code'
			);
		}

		if (payload?.sort?.includes('advanced_options_names')) {
			payload.sort = null;
		}
		// End temp fix for WEBAPPS-39108

		const response = await HistoryCommunication.getPrints(payload);
		const isSuccessful = HistoryCommunication.isSuccessful(response);
		const httpStatus = isSuccessful ? response.httpStatus : null;

		// data is `null` if http status code is 204 (No Content)
		const data =
			httpStatus === HttpStatusCode.OK ? Util.toCamelCase(response.data) : {};

		if (!isSuccessful) {
			showError({ response });
		}

		// format the print data
		let printDetails = isArray(data?.prints)
			? data.prints.map(print => Print(print))
			: [];

		// remove PostageCorrection prints
		printDetails = printDetails.filter(
			obj => obj?.printDetails?.printTypeName !== 'PostageCorrection'
		);

		return {
			nextPageRequest: response.data?.next_page_request,
			isNextPageRequest: isObject(data?.nextPageRequest),
			prints: printDetails,
		};
	};

	/**
	 * System makes a call to fetch prints data and stores the prints data and next-page request
	 * in the state object.
	 * @param {function} callback (Optional)
	 */
	searchPrints = async (callback, maxResults = 100) => {
		const {
			nextPageRequest: currentNextPageRequest,
			searchFilter,
			sortBy,
			activeMenuItem: { activeMenuItemId },
		} = this.state;

		this.setState({ isLoading: true });

		// deselect selected row in ag-grid
		if (this.gridApi) {
			this.gridApi.deselectAll();
		}

		// if "nextPageRequest" already exists, we can use this directly to
		// fetch next page of transactions without rebuilding the payload.
		const payload = isObject(currentNextPageRequest)
			? currentNextPageRequest
			: HistoryUtil.buildGetPrintsPayload(
					searchFilter,
					sortBy,
					activeMenuItemId
				);
		payload.max_results = maxResults;
		const { nextPageRequest, isNextPageRequest, prints } =
			await this.fetchPrints(payload);

		this.setState(
			{
				isLoading: false,
				nextPageRequest,
				isNextPageRequest,
				prints,
			},
			callback
		);
	};

	/**
	 * System makes a call to fetch prints data and stores the prints and nextPageRequest
	 * in the state object.
	 * @param {object} advancedSearchData
	 */
	advanceSearchPrints = async advancedSearchData => {
		this.setState(
			{
				searchFilter: isObject(advancedSearchData) ? advancedSearchData : {},

				// reset nextPageRequest before fetching new prints data
				nextPageRequest: null,
				activeMenuItem: {
					activeMenuItemId: ActiveMenuItemId.Search,
					leftMenuTitle: 'Search Result',
				},
				isAdvanceSearch: true,
				totalCount: null,
			},
			this.searchPrints
		);
	};

	/**
	 * Append current prints from the state to allPrints array to fully populate the grid for export.
	 */
	appendToAllPrints = () => {
		const { allPrints, prints } = this.state;
		allPrints.push(...prints);
		this.setState({ allPrints });
	};

	/**
	 * If not all prints have been loaded to the grid, continue loading until all prints are loaded.
	 * @param {function} callback
	 * @param {boolean} isRecursive true when this function is called recursively
	 */
	populateAllPrints = (callback, isRecursive) => {
		const { isNextPageRequest } = this.state;

		if (isNextPageRequest) {
			// if there are more prints to load, turn on loading spinner and set allPrints to prints
			if (!isRecursive) {
				// append existing prints in the grid to allPrints
				this.appendToAllPrints();
			}
			this.searchPrints(
				() => {
					this.appendToAllPrints();
					// recursively call this function to load the next page of prints
					this.populateAllPrints(callback, true);
				},
				500,
				true
			);
		} else {
			if (isRecursive) {
				const { allPrints } = this.state;
				this.gridApi.setRowData(allPrints);
			}
			callback();
		}
	};

	/**
	 * Update selected prints "costCodeId" and "costCodeName" data.
	 * @param {number} costCodeId
	 */
	setPrintsCostCode = async costCodeId => {
		const {
			modalContext: { showError },
		} = this.props;
		const { selectedPrints, costCodeOptions } = this.state;

		// a list of all selected prints' universalId
		const universalIds = selectedPrints.map(item => ({
			universalPostageId: `${item?.printDetails?.universalId}`,
			dateCreated: item?.transactionDate,
		}));
		const costCode = costCodeOptions.find(({ id }) => id === costCodeId);
		const response = await HistoryCommunication.setCostCode({
			costCodeID: isObject(costCode) ? costCodeId : null,
			costCodeName: isObject(costCode) ? costCode.code : 'None',
			costCodePrintInfoList: universalIds,
		});

		if (!HistoryCommunication.isSuccessful(response)) {
			return showError({
				response,
			});
		}

		const printsToUpdate = [];

		/**
		 * Modify the data from each rowNode and pass the updated data to `updateRowData` api call.
		 * AgGrid will locate the rowNode instance using the original data object.
		 * Hence, we should not be creating a new object here.
		 * https://www.ag-grid.com/archive/23.0.0/javascript-grid-data-update/
		 */
		this.gridApi.getSelectedNodes().forEach(({ data }) => {
			data.printDetails.costCodeId = costCodeId;
			data.printDetails.costCodeName = isObject(costCode) ? costCode.code : '';

			printsToUpdate.push(data);
		});

		// refresh grid ui
		this.gridApi.updateRowData({
			update: printsToUpdate,
		});
	};

	/**
	 * Set the new cost codes options passed from ManageCostCodesModal's callback.
	 * @param {array} costCodes
	 */
	onUpdateCostCodes = costCodes => {
		this.setState({
			costCodeOptions: Util.toCamelCase(costCodes),
		});
	};

	showTutorialGuide = () => {
		const { modalContext, layoutContext } = this.props;
		const { PostageToolsTutorialDone } = Session.getPreferences();

		if (PostageToolsTutorialDone !== '1' && layoutContext.isDesktop) {
			modalContext.showTutorial({
				id: 'History',
				guide: Guide,
				onEnd: () => {
					Logger.logToServer({
						category: 'tutorial_info',
						tutorialType: 'welcome',
					});

					// update the userPrefs so that tutorial modal won't show again
					Session.savePreferences([
						{
							Name: 'PostageToolsTutorialDone',
							Value: '1',
						},
					]);
				},
			});
		}
	};

	onMenuItemClick = activeMenuItem => {
		const { activeMenuItemValue, activeMenuItemId } = activeMenuItem || {};
		const { isAdvanceSearch } = this.state;

		if (activeMenuItemValue === DateRange.Custom) {
			// open "Advanced Search" modal with Date Range Custom automatically selected
			this.onAdvancedSearchTrigger({
				dateRangeValue: activeMenuItemValue,
			});
		} else if (
			activeMenuItemId === ActiveMenuItemId.Search &&
			isAdvanceSearch
		) {
			this.setState(
				{
					activeMenuItem,
					totalCount: null,
					nextPageRequest: null,
				},
				this.searchPrints
			);
		} else {
			this.onSearchPrintsTrigger(activeMenuItem);
		}
	};

	onSearchPrintsTrigger = activeMenuItem => {
		this.setState(
			{
				activeMenuItem,
				nextPageRequest: null, // reset request to remove all grid data for new data set
				searchFilter:
					HistoryUtil.buildSearchFilterByActiveMenuItem(activeMenuItem),
				totalCount: null,
				isAdvanceSearch: false,
			},
			this.searchPrints
		);
	};

	onAdvancedSearchTrigger = ({ dateRangeValue }) => {
		const { users, costCodeOptions } = this.state;
		const {
			modalContext: { showModal },
		} = this.props;

		showModal({
			component: AdvancedSearchModal,
			data: {
				users,
				searchPrints: this.advanceSearchPrints,
				dateRangeValue,
				costCodeOptions,
			},
		});
	};

	onGridReady = ({ api, sortModel }) => {
		this.gridApi = api;

		// make the api call only if user can use cost codes
		if (Session.hasCapability(Capability.CostCodes)) {
			this.fetchCostCodes();
		}

		if (Session.hasCapability(InternalCapability.ManageUsers)) {
			this.fetchUsers();
		}

		/**
		 * Dev note:
		 * To sort XXX column by default, set `sort` value to either "asc" or "desc" to
		 * that column config object in columnDefs array, i.e.
		 *
		 * [
		 *     {
		 *         colId: 'created_date',
		 *         sort: 'desc',
		 *         ...
		 *     },
		 *     ...
		 * ]
		 *
		 * Not all columns are sortable; only the columns with colId are sortable.
		 */

		this.setState(
			{
				sortBy: HistoryUtil.buildSortBy(sortModel),
			},
			() => {
				this.searchPrints(() => {
					this.showTutorialGuide();
				});
			}
		);
	};

	onSortChanged = sortModel => {
		this.setState(
			{
				nextPageRequest: null,
				sortBy: HistoryUtil.buildSortBy(sortModel),
			},
			this.searchPrints
		);
	};

	/**
	 * This function is a callback from AgGrid that gives us all the information from the selected rows.
	 * { data } is the information from the selection that we want to know, so we will extract it
	 * and provide it to the state via selectedPrints.
	 * We will display Details Panel whenever there is a selection
	 */
	onSelectionChanged = selectedPrints => {
		const {
			layoutContext: { setHidden },
		} = this.props;

		this.setState(
			{
				selectedPrints: selectedPrints.map(({ data }) => data),
			},
			setHidden(RegionType.East, selectedPrints.length === 0)
		);
	};

	onRowDataUpdated = totalRowDataCount => {
		if (totalRowDataCount < 0) {
			this.setState({ totalCount: null });
		} else {
			this.setState({ totalCount: totalRowDataCount });
		}
	};

	onUpgradePlanCallback = () => {
		if (Session.hasCapability(Capability.HistoryReturnLabel)) {
			// continue with return label flow if upgrade is successful
			this.continueReturnLabelFlow();
		}
	};

	returnLabelTriggerTracking = userAction => {
		const { customerDetails = {} } = Session.getAccountInfo();
		const customerEmail = customerDetails?.email;

		// EVENT USED BY MARKETING TEAM
		AnalyticsAgent.trackEvent(
			'upgradePromptReturnLabelHistory',
			{
				pointOfOrigin: this.upgradeTrigger,
				userAction,
				email: customerEmail,
			},
			AnalyticsAgent.agent.segment
		);
	};

	continueReturnLabelFlow = async () => {
		const { modalContext, startVerification } = this.props;
		const { selectedPrints, showBillingStatusForPrint, showEmailVerification } =
			this.state;

		if (showBillingStatusForPrint) {
			modalContext.showModal({
				component: BillingStatusModal,
			});
			return;
		}

		if (showEmailVerification) {
			modalContext.showModal({
				component: AccountVerificationModal,
				data: {
					completionCallback: this.accountVerificationCallback,
					emailOnly: true,
				},
			});
			return;
		}

		if (selectedPrints.length > 1) {
			return modalContext.showError({
				data: {
					title: 'Error',
					description: `
							<p>
								Only one return label can be created at a time.
							</p>
						`,
					primaryButtonText: 'Close',
					handleType: 'custom',
				},
			});
		}

		const { showPlaidVerification = true } = this.props.flags || {};

		if (!showPlaidVerification) {
			this.showCreateReturnLabelModal();
			return;
		}

		// Fraud gate: block until the account verifies identity via Plaid before
		// its first print, if it hasn't touched its reseller-granted free
		// postage yet. Plaid's own hosted Link UI opens directly — no app modal
		// in between. On success, continues straight to
		// showCreateReturnLabelModal (never re-enters this flow) so it can't
		// re-trigger Plaid or race the shared IDV-status cache.
		const { shouldShowVerificationModal, isRestricted } =
			await Session.shouldShowVerificationModal();

		if (isRestricted) {
			Logger.logToServer({
				category: 'identity_verification',
				action: 'payment_server_error_modal_displayed',
				page: 'history',
			});
			modalContext.showError({
				data: {
					title: 'Error',
					description: `
							<p>Unable to Print</p>
							<p>Payment Server Error occured</p>
						`,
					primaryButtonText: 'Close',
					handleType: 'custom',
				},
			});
			return;
		}

		if (shouldShowVerificationModal) {
			Logger.logToServer({
				category: 'identity_verification',
				action: 'verification_modal_displayed',
				page: 'history',
			});
			startVerification({
				onSuccess: result => {
					Logger.logToServer({
						category: 'identity_verification',
						action: 'verification_flow_completed',
						outcome: 'success',
						status: result.status,
						trustLevel: result.trustLevel,
						page: 'history',
					});
					this.showCreateReturnLabelModal();
				},
				onCancel: result => {
					Logger.logToServer({
						category: 'identity_verification',
						action: 'verification_flow_completed',
						outcome: 'cancelled',
						status: result.status,
						trustLevel: result.trustLevel,
						page: 'history',
					});
					modalContext.showError({
						data: {
							title: 'Verification required',
							description:
								'You must verify your identity before printing your return label.',
							primaryButtonText: 'OK',
							handleType: 'custom',
						},
					});
				},
				onFailure: result => {
					Logger.logToServer({
						category: 'identity_verification',
						action: 'verification_flow_completed',
						outcome: 'error',
						status: result.status,
						trustLevel: result.trustLevel,
						page: 'history',
					});
					modalContext.showError({
						data: {
							title: 'Verification failed',
							description: 'Identity verification failed. Please try again.',
							primaryButtonText: 'OK',
							handleType: 'custom',
						},
					});
				},
				onError: (_error, result) => {
					Logger.logToServer({
						category: 'identity_verification',
						action: 'verification_flow_completed',
						outcome: 'error',
						status: result.status,
						trustLevel: result.trustLevel,
						page: 'history',
					});
					modalContext.showError({
						data: {
							title: 'Verification failed',
							description: 'Identity verification failed. Please try again.',
							primaryButtonText: 'OK',
							handleType: 'custom',
						},
					});
				},
			});
			return;
		}

		this.showCreateReturnLabelModal();
	};

	showCreateReturnLabelModal = () => {
		const { modalContext } = this.props;
		const { selectedPrints, costCodeOptions } = this.state;

		modalContext.showModal({
			component: CreateReturnLabelModal,
			data: {
				costCodeOptions,
				selectedPrints,
				showUpgradeModal: this.showUpgradeModal,
			},
		});
	};

	// Handles return label creation. Shows upgrade modal if user lacks required capability,
	// otherwise continues to return label flow.
	onClickCreateReturnLabelButton = eventTriggerOrigin => {
		const canPrintReturnLabel = Session.hasCapability(
			Capability.HistoryReturnLabel
		);

		this.trackSegmentEvent('create-return-label-menu-button');

		if (canPrintReturnLabel) {
			this.continueReturnLabelFlow();
		} else {
			this.showUpgradeModal({
				eventTriggerOrigin,
				upgradePrompt: 'upgradePromptReturnLabelHistory',
			});
		}
	};

	// Displays upgrade plan modal with specified configuration for plan upgrades.
	showUpgradeModal = ({ eventTriggerOrigin, upgradePrompt, planId = null }) => {
		const { modalContext } = this.props;
		const { showModal } = modalContext;
		const upgradePlan = planId
			? BillingPlans.getPlanByPlanId(planId)
			: BillingPlans.getNextUpgradePlan();

		this.upgradeTrigger = eventTriggerOrigin;

		showModal({
			component: UpgradePlanModal,
			data: {
				upgradePlan,
				upgradePrompt,
				triggerFeature: eventTriggerOrigin,
				onUpgradePlanCallback: this.onUpgradePlanCallback,
				segmentTrackingCallback: this.returnLabelTriggerTracking,
			},
			closeCallback: closeType => {
				if (closeType === ModalButtonActionType.Ok) {
					this.returnLabelTriggerTracking('closeModal');
				}
			},
		});
	};

	// show "Manage Cost Codes" modal
	onClickManageCostCodes = () => {
		const { modalContext } = this.props;

		this.trackSegmentEvent('manage-cost-codes-menu-button');

		modalContext.showModal({
			component: ManageCostCodesModal,
			data: {
				callback: this.onUpdateCostCodes,
			},
		});
	};

	/**
	 * returns startDate and endDate for Date Printed column current view.
	 * returns today's date when grid is empty.
	 * @returns {object}
	 */
	getDateRangeForCurrentView = () => {
		const rows = [];
		this.gridApi.forEachNode(({ data }) => {
			const { transactionDate } = data;
			if (transactionDate) {
				rows.push(
					transactionDate.toLocaleDateString().replace(/[^a-zA-Z0-9]/g, '.')
				);
			}
		});
		const { 0: startDate, [rows.length - 1]: endDate } = rows.sort(
			(a, b) => new Date(a) - new Date(b)
		);
		const today = new Date().toLocaleDateString().replace(/[^a-zA-Z0-9]/g, '.');
		return {
			startDate: startDate || today,
			endDate: endDate || today,
		};
	};

	/**
	 * Exports the history grid to a CSV file with header row.
	 */
	handleExportToCsv = () => {
		Logger.saveActionData('History Toolbar Export Button Clicked');
		Logger.logToServer({
			category: 'ui_interaction',
			action: 'click',
			ui_element: 'export_button',
		});

		this.populateAllPrints(() => {
			const { accountId } = Session.getAccountInfo();
			const { startDate, endDate } = this.getDateRangeForCurrentView();
			const findNumber = /^-?\d+(\.\d+)?$/;
			const dateFields = [
				'created_date',
				'ship_date',
				'order_date',
				'delivered_date',
				'refund_requested_date',
			];

			this.gridApi.exportDataAsCsv({
				fileName: `PrintHistory_${accountId}_${startDate}_to_${endDate}.csv`,
				processCellCallback: ({ column: { colId }, value }) => {
					if (
						colId === 'tracking_number' ||
						colId === 'to_address_postal_code' ||
						colId === 'origin_zip'
					) {
						return findNumber.test(value)
							? `="${value}"`
							: neutralizeCsvFormula(value);
					} else if (dateFields.includes(colId)) {
						if (!value) {
							return value;
						}
						return moment(value).format('MM/DD/YYYY');
					} else {
						return neutralizeCsvFormula(value);
					}
				},
			});
			// reset allPrints to empty array
			this.setState({ allPrints: [] });
		});
	};

	// show File Claim modal
	onFileClaimClick = () => {
		const { selectedPrints } = this.state;
		const {
			modalContext: { showModal, showError },
		} = this.props;
		const accountInfo = Session.getAccountInfo();
		const address = accountInfo?.customerDetails?.address || {};
		const customerId = selectedPrints[0]?.custId || '';
		const dateMailed =
			selectedPrints[0]?.transactionDate.toLocaleDateString() || '';
		const email = accountInfo?.customerDetails?.email || '';
		const print = selectedPrints[0]?.printDetails || {};
		const universalPostageId =
			selectedPrints[0]?.printDetails?.universalId || '';

		this.trackSegmentEvent('file-claim-menu-button');

		if (print.isPgInsured) {
			if (print.isGap) {
				// show ParcelGuard / GAP/GP claim form
				showModal({
					component: FileClaimModal,
					data: {
						address,
						customerId,
						dateMailed,
						email,
						print,
						universalPostageId,
					},
				});
			} else {
				// open ParcelGuard insurance claim form in new tab
				openParcelGuardClaimForm(universalPostageId, showError);
			}
		} else {
			// show insurance claim form
			showModal({
				component: InsuranceClaimFormModal,
				data: {
					address,
					customerId,
					dateMailed,
					email,
					print,
					universalPostageId,
				},
			});
		}

		Logger.logToServer({
			category: 'button_click',
			action: 'file_claim',
		});
	};

	// show "Check Claim Status" modal for ParcelGuard insured orders
	onCheckClaimStatusClick = () => {
		const { selectedPrints } = this.state;
		const {
			modalContext: { showModal },
		} = this.props;
		const universalPostageId =
			selectedPrints[0]?.printDetails?.universalId || '';

		this.trackSegmentEvent('check-claim-status-menu-button');

		showModal({
			component: CheckClaimStatusModal,
			data: {
				universalPostageId,
			},
		});

		Logger.logToServer({
			category: 'button_click',
			action: 'check_claim_status',
		});
	};

	onEmailTrackingInfoClick = () => {
		let body = '',
			checkStatusUrl;

		const subject = 'Shipment Notice:  A package has been shipped to you',
			{ selectedPrints } = this.state;

		const {
			transactionDate,
			printDetails: { serviceTypeName, trackingNumber, toAddress },
		} = selectedPrints[0];

		const deliverAddress = Util.concatenateToAddress(toAddress);

		this.trackSegmentEvent('email-tracking-menu-button');

		if (selectedPrints) {
			body =
				'A package was shipped to you via the U.S. Postal Service and will be delivered to:\r\n\r\n';
			body += deliverAddress
				.replace(/<br\s*[/]?>/gi, '\r\n')
				.replace(/&#39;/gi, "'");

			if (transactionDate) {
				body += `\r\n\r\nShipment Date: ${moment(transactionDate).format('MMMM D, YYYY')}`;
			}

			if (serviceTypeName) {
				body += `\r\nMail Class: ${serviceTypeName.replace('(R)', '')}`;
			}

			if (trackingNumber) {
				if (!trackingNumber.startsWith('400')) {
					body += `\r\nTracking Number: ${trackingNumber}`;
				}

				checkStatusUrl = `http://www.stamps.com/shipstatus/submit/?confirmation=${trackingNumber}`;
				body += `\r\n\r\nCheck the package status with Stamps.com:\r\n${checkStatusUrl}`;
			}

			body += `\r\n\r\nThe shipment information contained in this email is provided by Stamps.com.\r\nFor questions about this package, please contact the \r\nsender of this email or the U.S. Postal Service\r\n`;
			body +=
				'============================================================================';
			body += `\r\nPrint official USPS postage from your own printer with Stamps.com, and \r\neliminate trips to the Post Office forever!`;
			body +=
				'\r\nSign up at http://www.stamps.com/freeoffer and receive our special \r\nintroductory offer. Try it risk free today!';
			body +=
				'\r\n============================================================================';
		}

		window.open(
			`mailto:?${Util.toQueryString({ subject, Body: body })}`,
			'_top'
		);
	};

	trackSegmentEvent = dataqa => {
		AnalyticsAgent.trackSegmentEvent({
			trackingData: {
				field_type: 'button',
				field_name_dataqa: { dataqa },
			},
			eventType: SegmentEventTypes.UserInteraction,
		});
	};

	onRefreshGridAPI = () => {
		this.setState(
			{
				totalCount: null,
				nextPageRequest: null,
			},
			this.searchPrints
		);
	};

	showPDFPreviewWindow = () => {
		const {
			modalContext: { showModal },
		} = this.props;

		showModal({
			component: PdfPreviewModal,
		});
	};

	setGridLoader = value => {
		this.setState({ isLoading: value });
	};

	render() {
		const {
			totalCount,
			activeMenuItem,
			selectedPrints,
			prints,
			pageSize,
			costCodeOptions,
			users,
			isLoading,
			nextPageRequest,
			isNextPageRequest,
			headerTrigger,
		} = this.state;
		const { IsInWinClient } = Session.getSettings();
		const westCollapsiblePanelCls = classnames({
			'win-client': IsInWinClient === true,
		});

		return (
			<>
				{/* north region */}
				<TopPanel region={RegionType.North}>
					<Header />
					<Analytics />
				</TopPanel>
				{/* Left */}
				<CollapsibleSidePanel
					region={RegionType.West}
					title={activeMenuItem.leftMenuTitle}
					className={westCollapsiblePanelCls}
				>
					<LeftMenu
						activeMenuItem={activeMenuItem}
						totalCount={totalCount}
						users={users}
						getDefaultActiveMenuItem={this.getDefaultActiveMenuItem}
						onMenuItemClick={this.onMenuItemClick}
						onAdvancedSearchTrigger={this.onAdvancedSearchTrigger}
					/>
				</CollapsibleSidePanel>
				{/* center */}
				<CenterPanel region={RegionType.Center}>
					<MarketingCampaign />
					<Toolbar
						selectedPrints={selectedPrints}
						costCodeOptions={costCodeOptions}
						fetchPrints={this.fetchPrints}
						prints={prints}
						setPrintsCostCode={this.setPrintsCostCode}
						onUpdateCostCodes={this.onUpdateCostCodes}
						onClickCreateReturnLabelButton={this.onClickCreateReturnLabelButton}
						onClickManageCostCodes={this.onClickManageCostCodes}
						handleExportToCsv={this.handleExportToCsv}
						onRefreshGridAPI={this.onRefreshGridAPI}
						headerTrigger={headerTrigger}
						activeMenuItem={activeMenuItem}
						setGridLoader={this.setGridLoader}
					/>
					<Grid
						prints={prints}
						onSortChanged={this.onSortChanged}
						onSelectionChanged={this.onSelectionChanged}
						onGridReady={this.onGridReady}
						onRowDataUpdated={this.onRowDataUpdated}
						pageSize={pageSize}
						fetchPrints={this.searchPrints}
						nextPageRequest={nextPageRequest}
						isNextPageRequest={isNextPageRequest}
						suppressLoadingOverlay
					/>
					{isLoading && <Spinner />}
				</CenterPanel>
				<CollapsibleSidePanel
					region={RegionType.East}
					title="Print Details"
					hideCollapsibleButton
				>
					<Details
						selectedPrints={selectedPrints}
						costCodeOptions={costCodeOptions}
						setPrintsCostCode={this.setPrintsCostCode}
						onClickCreateReturnLabelButton={this.onClickCreateReturnLabelButton}
						onClickManageCostCodes={this.onClickManageCostCodes}
						onFileClaimClick={this.onFileClaimClick}
						onCheckClaimStatusClick={this.onCheckClaimStatusClick}
						onEmailTrackingInfoClick={this.onEmailTrackingInfoClick}
						showUpgradeModal={this.showUpgradeModal}
					/>
				</CollapsibleSidePanel>
			</>
		);
	}
}
export default withLDConsumer()(withToast(withIdentityVerification(MainView)));
